import { useState, useEffect, useCallback } from "react";
import Loading from "../Common/Loading";

interface Guardian {
  name: string;
  relation: string;
  mobile: string;
  primaryContact: boolean;
  fluentEnglish: boolean;
  installationContact: boolean;
}

interface Family {
  referralName: string;
  freePeriod: string;
  freePeriodDate: string;
  consultant: string;
  otherLanguage: string;
  surname: string;
  agreementNumber: string;
  homePhone: string;
  workPhone: string;
  familyComments: string;
  guardian1: Guardian;
  guardian2: Guardian;
}

interface Student {
  studentFocus: boolean;
  studentFName: string;
  gender: string;
  year: number | null;
  englishFocus: boolean;
  englishLevel: string;
  mathsFocus: boolean;
  mathsLevel: string;
  writingFocus: boolean;
  mdCompleted: boolean;
  homeSchooling: string;
  parentsSeparated: string;
  adhd: boolean;
  add: boolean;
  asd: boolean;
  dyslexia: boolean;
  hyperactive: boolean;
  specialNeedsOthers: string;
  studentKeen: boolean;
  lowConfidence: boolean;
  studentReluctant: boolean;
  lowAttentionSpan: boolean;
  examAnxiety: boolean;
  catchUp: boolean;
  getAhead: boolean;
  extension: boolean;
  selectiveScholarshipPrep: boolean;
  homeworkConfidence: boolean;
  naplanPrep: boolean;
  recommendation: string;
  additionalComments: string;
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const LEVEL_OPTIONS = ["Please Select", "Advance", "Doing OK", "Struggling", "Not sure"];

const RECOMMENDATION_OPTIONS = [
  "Please Select",
  "4*15/Wk-(2*maths/2*english)OR(1 subject)",
  "4*20/Wk-(2*maths/2*english)OR(1 subject)",
  "3*30/Wk-(2*maths/2*english)OR(1 subject)",
  "3*15/Wk-(2*maths/1*english)OR(1 subject)",
  "3*15/Wk-(1*maths/2*english)OR(1 subject)",
  "3*20/Wk-(2*maths/1*english)OR(1 subject)",
  "3*20/Wk-(1*maths/2*english)OR(1 subject)",
  "3*30/Wk-(2*maths/1*english)OR(1 subject)",
  "3*30/Wk-(1*maths/2*english)OR(1 subject)",
];

const EMPTY_GUARDIAN: Guardian = {
  name: "", relation: "", mobile: "",
  primaryContact: false, fluentEnglish: false, installationContact: false,
};

const EMPTY_FAMILY: Family = {
  referralName: "", freePeriod: "", freePeriodDate: "", consultant: "",
  otherLanguage: "", surname: "", agreementNumber: "",
  homePhone: "", workPhone: "", familyComments: "",
  guardian1: EMPTY_GUARDIAN, guardian2: EMPTY_GUARDIAN,
};

const CSS = `
  /* === Base (from original CSS body rule) === */
  * { box-sizing: border-box; }
  body {
    background: white;
    padding: 0px;
    color: #666666;
    font-weight: bold;
    margin: 0;
  }

  /* === .container === */
  .asi-container {
    font-family: Arial, Helvetica, sans-serif;
    box-shadow: 0 0 0 1px #666666;
    margin: 30px auto;
    border-radius: 4px;
    border: 1px solid #131212;
    background: white;
    outline: none;
    max-width: 1300px;
    padding-bottom: 20px;
  }

  /* === .form-header === */
  .form-header {
    background-color: rgb(245, 250, 252);
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    font-weight: bold;
    font-size: 30px;
  }
  .form-header h3 {
    font-size: 30px;
    text-align: center;
    color: #666666;
    padding: 20px 0 10px;
    border-bottom: 2px solid #eee4e4;
    margin: 20px 0px 10px;
    font-weight: bold;
    font-family: Arial, Helvetica, sans-serif;
  }

  /* === .form-header-1 === */
  .form-header-1 h3 {
    font-size: 24px;
    text-align: left;
    color: #666666;
    padding: 5px 0;
    border-bottom: 1px solid #eee4e4;
    font-family: Arial, Helvetica, sans-serif;
    font-weight: bold;
    margin: 10px 0 8px;
  }
  .form-header-1 h4 {
    font-weight: bold;
    font-size: 17px;
    color: #666666;
    font-family: Arial, Helvetica, sans-serif;
    margin: 8px 0 6px;
  }

  /* === h4 global === */
  h4 { font-weight: bold; font-size: 17px; }

  /* === .label-title === */
  .label-title {
    color: #666666;
    text-align: start;
    font-weight: bold;
    display: block;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 17px;
    padding: 0px;
    margin-bottom: 2px;
  }
  .label-title-one { font-size: 17px; font-weight: bold; color: #666666; font-family: Arial, Helvetica, sans-serif; display: block; margin-bottom: 2px; }
  .label-title-four {
    color: #666666;
    text-align: start;
    font-weight: bold;
    display: block;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 17px;
    padding: 0px 20px 20px 20px;
  }

  /* === .form-inline / .form-group === */
  .form-inline {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px 12px;
    padding: 8px 15px;
  }
  .form-group { display: inline-flex; flex-direction: column; align-items: flex-start; }
  .form-group-last { padding: 0px; float: right; margin: 0px; }

  /* === inputs === */
  .form-input {
    font-size: 14px;
    box-sizing: border-box;
    width: 150px;
    height: 30px;
    padding-left: 10px;
    padding-right: 10px;
    color: #274368;
    text-align: left;
    border: 1px solid #131212;
    border-radius: 4px;
    background: rgb(250, 253, 253);
    outline: none;
    font-family: Arial, Helvetica, sans-serif;
  }
  .form-input:disabled { background: rgb(240,242,242); color: #888; cursor: not-allowed; }
  .form-input-two {
    font-size: 16px;
    box-sizing: border-box;
    width: 300px;
    height: 30px;
    padding-left: 10px;
    padding-right: 10px;
    padding-top: 20px;
    padding-bottom: 30px;
    color: #274368;
    text-align: left;
    border: 1px solid #131212;
    border-radius: 4px;
    background: rgb(249, 250, 250);
    outline: none;
    font-family: Arial, Helvetica, sans-serif;
  }
  .form-input-two:disabled { background: rgb(240,242,242); color: #888; cursor: not-allowed; }
  .form-input-four {
    font-size: 14px;
    box-sizing: border-box;
    width: 150px;
    height: 30px;
    padding-left: 10px;
    padding-right: 10px;
    color: #274368;
    text-align: left;
    border: 1px solid #131212;
    border-radius: 4px;
    background: rgb(250, 253, 253);
    outline: none;
    font-family: Arial, Helvetica, sans-serif;
  }
  .form-control {
    font-size: 16px;
    box-sizing: border-box;
    width: 100%;
    padding: 10px;
    color: #274368;
    border: 1px solid #131212;
    border-radius: 4px;
    background: rgb(249, 250, 250);
    outline: none;
    font-family: Arial, Helvetica, sans-serif;
    resize: vertical;
  }

  /* === select === */
  .form-select {
    font-size: 14px;
    height: 30px;
    padding: 0 8px;
    color: #274368;
    border: 1px solid #131212;
    border-radius: 4px;
    background: rgb(250, 253, 253);
    outline: none;
    font-family: Arial, Helvetica, sans-serif;
    cursor: pointer;
  }
  .form-select-wide { min-width: 340px; }

  /* === checkbox sizing (from CSS id selectors, generalised) === */
  .chk-student, .chk-english-focus, .chk-maths-focus, .chk-writing-focus, .chk-md-completed {
    height: 25px !important;
    width: 100px !important;
    vertical-align: -webkit-baseline-middle;
    cursor: pointer;
  }
  .chk-special-needs, .chk-additional-info {
    height: 25px !important;
    width: 50px !important;
    vertical-align: middle;
    cursor: pointer;
  }
  .chk-goals {
    height: 25px !important;
    width: 30px !important;
    vertical-align: middle;
    cursor: pointer;
  }
  .chk-guardian {
    height: 25px;
    width: 100px;
    vertical-align: -webkit-baseline-middle;
    cursor: pointer;
  }

  /* === radiobtnlist === */
  .radiobtnlist { display: inline-flex; gap: 0 16px; align-items: center; }
  .radiobtnlist label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-right: 8px;
    color: Black;
    margin-left: 3px;
    font-weight: bold;
    font-size: 15px;
    cursor: pointer;
  }
  input[type=radio] { float: left; clear: none; margin: 2px 0 0 2px; cursor: pointer; }

  /* === expand button (replaces img toggle from JS) === */
  .btn-expand {
    width: 32px;
    height: 32px;
    border: 1px solid #666;
    border-radius: 3px;
    background: rgb(250,253,253);
    cursor: pointer;
    font-size: 13px;
    font-weight: bold;
    color: #274368;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    flex-shrink: 0;
    align-self: center;
    font-family: Arial, Helvetica, sans-serif;
  }
  .btn-expand:hover { background: #d2e5ec; }

  /* === .row-1 / .row-one / .col-sm-* === */
  .row-1 { padding: 10px; font-size: 17px; }
  .row-one { margin: 0px; padding: 0px; }
  .row-2 { padding: 10px 15px; }
  .col-sm-1 { display: inline-flex; align-items: center; }
  .col-sm-2 { display: inline-flex; align-items: center; padding-right: 10px; }

  /* === .lable-two === */
  .lable-two {
    padding: 0;
    display: flex;
    flex-direction: row;
    font-family: Arial, Helvetica, sans-serif;
    float: left;
    clear: none;
    font-size: 17px;
    font-weight: bold;
    color: #666;
    margin-right: 6px;
  }

  /* === student card / collapse === */
  .student-item {
    border-bottom: 1px solid #eee4e4;
    padding: 6px 0 4px;
  }
  .collapse-panel { display: none; padding: 10px 0 4px 0; }
  .collapse-panel.open { display: block; }

  /* === .btn-btn-1 / .btn-btn-2 === */
  .btn-btn-1 {
    border-radius: 3px;
    border: 1px solid #666666;
    color: rgb(248, 252, 253);
    background-color: #bbb7b7;
    box-shadow: 0 0 0 1px #666666;
    font-size: 17px;
    box-sizing: border-box;
    width: 133.33px;
    height: 43px;
    padding: 6px 12px;
    margin: 0px 10px 10px;
    cursor: pointer;
    font-family: Arial, Helvetica, sans-serif;
    font-weight: bold;
  }
  .btn-btn-2 {
    border-radius: 3px;
    border: 1px solid #131212;
    color: rgb(244, 244, 250);
    background-color: #0e2343;
    box-shadow: 0 0 0 1px #0e2343;
    font-size: 17px;
    box-sizing: border-box;
    width: 133.33px;
    height: 43px;
    padding: 6px 12px;
    margin: 0px 10px 10px;
    cursor: pointer;
    font-family: Arial, Helvetica, sans-serif;
    font-weight: bold;
  }
  .btn-btn-2:disabled { opacity: 0.6; cursor: not-allowed; }

  /* === .form-footer === */
  .form-footer {
    clear: both;
    color: #274368;
    background-color: #f1f5f5;
    padding: 10px;
    border-radius: 4px;
    font-size: 14px;
    font-family: Arial, Helvetica, sans-serif;
    margin: 10px 15px 0;
  }

  /* === alert messages === */
  .alert-error {
    padding: 8px 14px;
    border-radius: 4px;
    font-size: 14px;
    background: #fff5f5;
    border: 1px solid #feb2b2;
    color: #c53030;
    margin: 10px 15px;
    font-family: Arial, Helvetica, sans-serif;
  }
  .alert-success {
    padding: 8px 14px;
    border-radius: 4px;
    font-size: 14px;
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    color: #276749;
    margin: 0;
    font-family: Arial, Helvetica, sans-serif;
  }
  .loading-msg {
    padding: 40px;
    text-align: center;
    color: #666;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 17px;
  }

  /* === hr === */
  hr { border: none; border-top: 1px solid #eee4e4; margin: 8px 0; }

  /* === action row === */
  .action-row {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    gap: 0;
    flex-wrap: wrap;
  }
`;


function getQueryParam(key: string): string {
  return new URLSearchParams(window.location.search).get(key) ?? "";
}

interface GuardianRowProps {
  guardian: Guardian;
  prefix: string;
  onChange: (g: Guardian) => void;
  showLabels: boolean;
  otherLang?: string;
  onOtherLangChange?: (v: string) => void;
}
function GuardianRow({ guardian, prefix, onChange, showLabels, otherLang = "", onOtherLangChange }: GuardianRowProps) {
  const upd = (patch: Partial<Guardian>) => onChange({ ...guardian, ...patch });
  return (
    <div className="form-inline">
      {/* Parent/Guardian */}
      <div className="form-group">
        {showLabels && <label className="label-title">Parent/Guardian</label>}
        {!showLabels && <label className="label-title-four">&nbsp;</label>}
        <input
          className={showLabels ? "form-input" : "form-input-four"}
          value={guardian.name}
          onChange={(e) => upd({ name: e.target.value })}
        />
      </div>
      {/* Relation */}
      <div className="form-group">
        {showLabels && <label className="label-title">Relation</label>}
        {!showLabels && <label className="label-title-four">&nbsp;</label>}
        <input
          className={showLabels ? "form-input" : "form-input-four"}
          value={guardian.relation}
          onChange={(e) => upd({ relation: e.target.value })}
        />
      </div>
      {/* Mobile */}
      <div className="form-group">
        {showLabels && <label className="label-title">Mobile#</label>}
        {!showLabels && <label className="label-title-four">&nbsp;</label>}
        <input
          className={showLabels ? "form-input" : "form-input-four"}
          value={guardian.mobile}
          onChange={(e) => upd({ mobile: e.target.value })}
        />
      </div>
      {/* Pri.Contact */}
      <div className="form-group">
        {showLabels && <label className="label-title">Pri.Contact</label>}
        {!showLabels && <label className="label-title-four">&nbsp;</label>}
        <input
          id={`${prefix}-pri`}
          type="checkbox"
          className="chk-guardian"
          checked={guardian.primaryContact}
          onChange={(e) => upd({ primaryContact: e.target.checked })}
        />
      </div>
      {/* Inst.Contact */}
      <div className="form-group">
        {showLabels && <label className="label-title">Inst.Contact</label>}
        {!showLabels && <label className="label-title-four">&nbsp;</label>}
        <input
          id={`${prefix}-inst`}
          type="checkbox"
          className="chk-guardian"
          checked={guardian.installationContact}
          onChange={(e) => upd({ installationContact: e.target.checked })}
        />
      </div>
      {/* F.English */}
      <div className="form-group">
        {showLabels && <label className="label-title">F.English</label>}
        {!showLabels && <label className="label-title-four">&nbsp;</label>}
        <input
          id={`${prefix}-eng`}
          type="checkbox"
          className="chk-guardian"
          checked={guardian.fluentEnglish}
          onChange={(e) => upd({ fluentEnglish: e.target.checked })}
        />
      </div>
      {/* Other Lang — first row only */}
      {showLabels && (
        <div className="form-group">
          <label className="label-title">Other Lang.</label>
          <input
            className="form-input"
            value={otherLang}
            onChange={(e) => onOtherLangChange?.(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

interface StudentCardProps {
  student: Student;
  index: number;
  onChange: (s: Student) => void;
}
function StudentCard({ student, index, onChange }: StudentCardProps) {
  // Directly replaces per-index jQuery toggle: $("#rptStudInfo_btnExp_N").click(...)
  const [expanded, setExpanded] = useState(false);
  const upd = (patch: Partial<Student>) => onChange({ ...student, ...patch });

  return (
    <div className="student-item">
      {/* ── Main row ── */}
      <div className="form-inline">
        {/* Expand button — replaces img src swap between Expand.gif / Collapse.gif */}
        <div className="form-group">
          <button
            id={`rptStudInfo_btnExp_${index}`}
            className="btn-expand"
            type="button"
            title={expanded ? "Collapse" : "Expand"}
            onClick={() => setExpanded((v) => !v)}
            style={{background:"#337ab7", border:"1px solid #2e6da4", color:"#fff"}}
          >
            {expanded ? "-" : "+"}
          </button>
        </div>

        {/* StudentFocus checkbox */}
        <div className="form-group">
          <div className="form-group-last">
            <input
              id={`rptStudInfo_chkStudents_${index}`}
              type="checkbox"
              className="chk-student"
              checked={student.studentFocus}
              onChange={(e) => upd({ studentFocus: e.target.checked })}
            />
          </div>
        </div>

        {/* Student Name */}
        <div className="form-group">
          <label className="label-title">Student Name</label>
          <input className="form-input" value={student.studentFName} disabled />
        </div>

        {/* Gender */}
        <div className="form-group">
          <label className="label-title">Gender</label>
          <input
            className="form-input"
            value={student.gender}
            onChange={(e) => upd({ gender: e.target.value })}
          />
        </div>

        {/* Year */}
        <div className="form-group">
          <label className="label-title">Year</label>
          <input className="form-input" value={student.year !== null ? String(student.year) : ""} disabled />
        </div>

        {/* English Focus */}
        <div className="form-group">
          <input
            id={`rptStudInfo_chkEnglishFocus_${index}`}
            type="checkbox"
            className="chk-english-focus"
            checked={student.englishFocus}
            onChange={(e) => upd({ englishFocus: e.target.checked })}
          />
        </div>

        {/* English level */}
        <div className="form-group">
          <label className="label-title">English</label>
          <select
            className="form-select"
            value={student.englishLevel}
            onChange={(e) => upd({ englishLevel: e.target.value })}
          >
            {LEVEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {/* Maths Focus */}
        <div className="form-group">
          <input
            id={`rptStudInfo_chkMathsFocus_${index}`}
            type="checkbox"
            className="chk-maths-focus"
            checked={student.mathsFocus}
            onChange={(e) => upd({ mathsFocus: e.target.checked })}
          />
        </div>

        {/* Maths level */}
        <div className="form-group">
          <label className="label-title">Maths</label>
          <select
            className="form-select"
            value={student.mathsLevel}
            onChange={(e) => upd({ mathsLevel: e.target.value })}
          >
            {LEVEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      {/* ── Secondary row: Writing / MD ── */}
      <div className="form-inline" style={{ paddingLeft: 60 }}>
        <div className="form-group">
          <input
            id={`rptStudInfo_chkWritingFocus_${index}`}
            type="checkbox"
            className="chk-writing-focus"
            checked={student.writingFocus}
            onChange={(e) => upd({ writingFocus: e.target.checked })}
          />
        </div>
        <div className="form-group">
          <label className="label-title">Writing Focus</label>
        </div>
        <div className="form-group">
          <input
            id={`rptStudInfo_chkMDCompleted_${index}`}
            type="checkbox"
            className="chk-md-completed"
            checked={student.mdCompleted}
            onChange={(e) => upd({ mdCompleted: e.target.checked })}
          />
        </div>
        <div className="form-group">
          <label className="label-title">MD Completed</label>
        </div>
      </div>

      <hr />

      {/* ── Collapsible panel — replaces #collapseExampleN toggle ── */}
      <div
        id={`collapseExample${index + 1}`}
        className={`collapse-panel${expanded ? " open" : ""}`}
      >
        {/* House Schooling */}
        <div className="form-inline">
          <h4>House schooling:</h4>
          <div className="radiobtnlist">
            {["Yes", "No"].map((val) => (
              <label key={val}>
                <input
                  type="radio"
                  name={`rdoHouseSchooling_${index}`}
                  value={val}
                  checked={student.homeSchooling === val}
                  onChange={() => upd({ homeSchooling: val })}
                />
                {val}
              </label>
            ))}
          </div>
        </div>

        {/* Parents Separated */}
        <div className="form-inline">
          <h4>Parent Separated:</h4>
          <div className="radiobtnlist">
            {["Yes", "No"].map((val) => (
              <label key={val}>
                <input
                  type="radio"
                  name={`rdoParentsSeparated_${index}`}
                  value={val}
                  checked={student.parentsSeparated === val}
                  onChange={() => upd({ parentsSeparated: val })}
                />
                {val}
              </label>
            ))}
          </div>
        </div>

        {/* Special Needs */}
        <div className="row-one">
          <div className="form-header-1"><h4>Special Needs:</h4></div>
          <div className="row-1">
            {(
              [
                ["adhd", "chk-special-needs", "ADHD"],
                ["add", "chk-special-needs", "ADD"],
                ["asd", "chk-special-needs", "ASD"],
                ["dyslexia", "chk-special-needs", "Dyslexia"],
                ["hyperactive", "chk-special-needs", "Hyperactive"],
              ] as [keyof Student, string, string][]
            ).map(([field, cls, label]) => (
              <span key={field} className="col-sm-1" style={{ marginRight: 6 }}>
                <input
                  id={`rptStudInfo_chk${field.charAt(0).toUpperCase() + field.slice(1)}_${index}`}
                  type="checkbox"
                  className={cls}
                  checked={student[field] as boolean}
                  onChange={(e) => upd({ [field]: e.target.checked })}
                />
                <label style={{ marginLeft: 4, fontSize: 17, fontWeight: "bold", color: "#666" }}>{label}</label>
              </span>
            ))}
            <span className="col-sm-2">
              <label className="lable-two">Other</label>
              <input
                id={`rptStudInfo_txtOther_${index}`}
                className="form-input"
                style={{ width: 100 }}
                value={student.specialNeedsOthers}
                onChange={(e) => upd({ specialNeedsOthers: e.target.value })}
              />
            </span>
          </div>
        </div>

        <br />

        {/* Additional Information */}
        <div className="row-one">
          <div className="form-header-1"><h4>Additional Information:</h4></div>
          <div className="row-1">
            {(
              [
                ["studentKeen", "Student Keen"],
                ["lowConfidence", "Low Confidence"],
                ["studentReluctant", "Student Reluctant"],
                ["lowAttentionSpan", "Low Attention"],
                ["examAnxiety", "Exam Anxiety"],
              ] as [keyof Student, string][]
            ).map(([field, label]) => (
              <span key={field} className="col-sm-1" style={{ marginRight: 6 }}>
                <input
                  id={`rptStudInfo_chk${field.charAt(0).toUpperCase() + field.slice(1)}_${index}`}
                  type="checkbox"
                  className="chk-additional-info"
                  checked={student[field] as boolean}
                  onChange={(e) => upd({ [field]: e.target.checked })}
                />
                <label style={{ marginLeft: 4, fontSize: 17, fontWeight: "bold", color: "#666" }}>{label}</label>
              </span>
            ))}
          </div>
        </div>

        <br /><br />

        {/* Student Goals */}
        <div className="row-one">
          <div className="form-header-1"><h4>Student Goals:</h4></div>
          <div className="row-1">
            {(
              [
                ["catchUp", "Catch Up"],
                ["getAhead", "Get Ahead"],
                ["extension", "Extension"],
                ["selectiveScholarshipPrep", "Selective / Scholarship Prep"],
                ["homeworkConfidence", "Homework Confidence"],
                ["naplanPrep", "NAPLAN Prep"],
              ] as [keyof Student, string][]
            ).map(([field, label]) => (
              <span key={field} className="col-sm-1" style={{ marginRight: 6 }}>
                <input
                  id={`rptStudInfo_chk${field.charAt(0).toUpperCase() + field.slice(1)}_${index}`}
                  type="checkbox"
                  className="chk-goals"
                  checked={student[field] as boolean}
                  onChange={(e) => upd({ [field]: e.target.checked })}
                />
                <label style={{ marginLeft: 4, fontSize: 17, fontWeight: "bold", color: "#666" }}>{label}</label>
              </span>
            ))}
          </div>
        </div>

        <br /><br />

        {/* Recommendation */}
        <div className="row-one">
          <div className="row-1" style={{ display: "inline-block" }}>
            <label
              htmlFor={`ddlOptions_${index}`}
              style={{ fontSize: 17, fontWeight: "bold", color: "#666", marginRight: 8, fontFamily: "Arial, Helvetica, sans-serif" }}
            >
              Recommendation:
            </label>
            <select
              id={`ddlOptions_${index}`}
              className="form-select form-select-wide"
              value={student.recommendation}
              onChange={(e) => upd({ recommendation: e.target.value })}
            >
              {RECOMMENDATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        {/* Additional Comments about child */}
        <div className="row-one">
          <div className="form-header-1"><h4>Additional Comments about child</h4></div>
          <textarea
            id={`rptStudInfo_txtComments_${index}`}
            className="form-control"
            rows={4}
            style={{ width: "100%", maxWidth: 1100 }}
            value={student.additionalComments}
            onChange={(e) => upd({ additionalComments: e.target.value })}
          />
        </div>
      </div>
      {/* end collapse panel */}
    </div>
  );
}


export default function AdditionalStudentsInfo() {
  const [family, setFamily] = useState<Family>(EMPTY_FAMILY);
  const [students, setStudents] = useState<Student[]>([]);
  const [referralVisible, setReferralVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  const schoolCode =
    getQueryParam("s") ||
    getQueryParam("S") ||
    getQueryParam("SchoolCode");

  const familyLogin =
    getQueryParam("f") ||
    getQueryParam("F") ||
    getQueryParam("FamilyCode");

  const adminLogin = getQueryParam("L");  

  const fetchData = useCallback(async () => {
    if (!schoolCode || !familyLogin) {
      setError("Missing required URL parameters (s, f). Please reopen from the correct link.");
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE_URL}api/additional-students-info/?s=${encodeURIComponent(schoolCode)}&f=${encodeURIComponent(adminLogin)}`
      );
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      const data = await res.json();
      setFamily({
        ...EMPTY_FAMILY, ...data.family,
        guardian1: { ...EMPTY_GUARDIAN, ...data.family?.guardian1 },
        guardian2: { ...EMPTY_GUARDIAN, ...data.family?.guardian2 },
      });
      setStudents(data.students ?? []);
      setReferralVisible(!!(data.family?.referralName));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [schoolCode, familyLogin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Save ────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}api/additional-students-info/save/?s=${encodeURIComponent(schoolCode)}&f=${encodeURIComponent(adminLogin)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ family, students }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error ?? `Server error ${res.status}`);
      }
      setSaveMsg("Saved successfully.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const updateStudent = (index: number, updated: Student) =>
    setStudents((prev) => prev.map((s, i) => (i === index ? updated : s)));

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────

  return (
    <>
      <style>{CSS}</style>

      <div className="asi-container">

        {/* ══ Page title ══ */}
        <div className="form-header">
          <h3>Additional Students Information</h3>
        </div>

        {loading && <Loading />}
        {error && <div className="alert-error">{error}</div>}

        {!loading && !error && (
          <>
            {/* ══ Referral / Free Period / Consultant ══ */}
            <div className="form-inline">
              {/* Referral checkbox — data-toggle collapse equivalent */}
              <div className="form-group">
                <label className="label-title">Referral</label>
                <input
                  id="chkReferral"
                  type="checkbox"
                  className="chk-guardian"
                  checked={referralVisible}
                  onChange={(e) => setReferralVisible(e.target.checked)}
                />
              </div>

              {/* Referral name — shown/hidden matching Bootstrap collapse */}
              {referralVisible && (
                <div className="form-group">
                  <label className="label-title">Referral name</label>
                  <input
                    id="txtRefName"
                    className="form-input"
                    value={family.referralName}
                    onChange={(e) => setFamily({ ...family, referralName: e.target.value })}
                  />
                </div>
              )}

              <div className="form-group">
                <label className="label-title">Free period</label>
                <input
                  id="txtFreePeriod"
                  className="form-input"
                  value={family.freePeriod}
                  onChange={(e) => setFamily({ ...family, freePeriod: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="label-title">Free period Date</label>
                <input
                  id="txtFreeperiodDate"
                  className="form-input"
                  value={family.freePeriodDate}
                  onChange={(e) => setFamily({ ...family, freePeriodDate: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="label-title">Consultant</label>
                <input
                  id="txtConsultant"
                  className="form-input"
                  value={family.consultant}
                  onChange={(e) => setFamily({ ...family, consultant: e.target.value })}
                />
              </div>
            </div>

            {/* ══ Parents / Guardians ══ */}
            <div className="form-header-1" style={{ padding: "0 15px" }}>
              <h3>Parents/Guardians Information</h3>
            </div>

            <GuardianRow
              guardian={family.guardian1}
              prefix="g1"
              onChange={(g) => setFamily({ ...family, guardian1: g })}
              showLabels={true}
              otherLang={family.otherLanguage}
              onOtherLangChange={(v) => setFamily({ ...family, otherLanguage: v })}
            />

            {/* Second guardian row — shown when both mother & father exist */}
            {(family.guardian2.name || family.guardian2.relation) && (
              <GuardianRow
                guardian={family.guardian2}
                prefix="g2"
                onChange={(g) => setFamily({ ...family, guardian2: g })}
                showLabels={false}
              />
            )}

            {/* ══ Students ══ */}
            <div className="form-header-1" style={{ padding: "0 15px" }}>
              <h3>Students Information</h3>
            </div>

            <div style={{ padding: "0 15px" }}>
              {students.length === 0 && (
                <p style={{ color: "#666", fontSize: 15, padding: "10px 0" }}>No student records found.</p>
              )}
              {students.map((s, i) => (
                <StudentCard key={i} student={s} index={i} onChange={(u) => updateStudent(i, u)} />
              ))}
            </div>

            <hr />

            {/* ══ Surname / Agreement Number ══ */}
            <div className="form-group" style={{ padding: "8px 15px" }}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <label className="label-title-one">Surname</label>
                  <input id="txtSurname" className="form-input-two" value={family.surname} disabled />
                </div>
                <div>
                  <label className="label-title-one">Agreement Number</label>
                  <input id="txtAgreementNumber" className="form-input-two" value={family.agreementNumber} disabled />
                </div>
              </div>
            </div>

            {/* ══ Home Phone / Work Phone ══ */}
            <div className="form-group" style={{ padding: "8px 15px" }}>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <label className="label-title-one">Home Phone</label>
                  <input id="txtHomePhone" className="form-input-two" value={family.homePhone} disabled />
                </div>
                <div>
                  <label className="label-title-one">Work Phone</label>
                  <input id="txtWorkPhone" className="form-input-two" value={family.workPhone} disabled />
                </div>
              </div>
            </div>

            {/* ══ Family Comment ══ */}
            <div className="row-2">
              <div className="form-header-1"><h3>Family comment</h3></div>
              <textarea
                id="txtFamilyComments"
                className="form-control"
                rows={4}
                style={{ width: "100%", maxWidth: 1100 }}
                value={family.familyComments}
                onChange={(e) => setFamily({ ...family, familyComments: e.target.value })}
              />
            </div>

            <hr />

            {/* ══ Save / Close ══ */}
            <div className="action-row" style={{display:"flex", justifyContent:"flex-end"}}>

              
              <button
                id="btnClose"
                className="btn-btn-1"
                type="button"
                onClick={() => window.close()}
              >
                Close
              </button>
              <button
                id="btnSave"
                className="btn-btn-2"
                onClick={handleSave}
                disabled={saving}
                type="button"
              >
                {saving ? "Saving…" : "Save"}
              </button>

              {saveMsg && <span className="alert-success">{saveMsg}</span>}
            </div>
          </>
        )}

        {/* ══ Footer ══ */}
        <div className="form-footer">©2023 - Kinetic Education</div>

      </div>
    </>
  );
}